import { useEffect, useState } from "react";
import { Redirect } from "wouter";
import { getMerchantToken, merchantFetch } from "../lib/merchantAuth";
import { MerchantNav } from "../components/MerchantNav";

/**
 * Merchant Surprise Pool page — Phase 19.
 *
 * One pool per tenant (D72). Rendered as a single column:
 *   • Pool header — name + enabled toggle + push toggle.
 *   • Entries list — display_label / kind / weight / enabled, with
 *     edit + delete actions inline.
 *   • Add-entry form — payload fields swap with the chosen kind.
 *     VOUCHER_REWARD's reward dropdown is populated from the tenant's
 *     existing rewards (server enforces tenant-scope; the dropdown
 *     just keeps the cashier from typing UUIDs).
 *   • Preview — renders today's deterministic pick for a sample
 *     customer UUID. Read-only on the server.
 *
 * No analytics chart yet — the analytics endpoint is wired (used by
 * the test suite + future Phase 20 reporting page) but the cashier UI
 * currently surfaces only the totals from the same response.
 */

type EntryKind =
  | "POINTS_BONUS"
  | "STAMPS_BONUS"
  | "VOUCHER_REWARD"
  | "NO_PRIZE";

interface PoolWire {
  id: string;
  tenantId: string;
  name: string;
  enabled: boolean;
  surprisePushEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  enabledEntryCount: number;
}

interface EntryWire {
  id: string;
  poolId: string;
  entryKind: EntryKind;
  entryPayload: Record<string, unknown>;
  weight: number;
  enabled: boolean;
  displayLabel: string;
  createdAt: string;
}

interface RewardLite {
  id: string;
  name: string;
  active: boolean;
}

const KIND_OPTIONS: { value: EntryKind; label: string }[] = [
  { value: "POINTS_BONUS", label: "Bonus points" },
  { value: "STAMPS_BONUS", label: "Bonus stamps" },
  { value: "VOUCHER_REWARD", label: "Voucher" },
  { value: "NO_PRIZE", label: "No prize (honest)" },
];

export function MerchantSurprisePoolPage() {
  if (!getMerchantToken()) return <Redirect to="/merchant/login" />;
  return <MerchantSurprisePoolInner />;
}

function MerchantSurprisePoolInner() {
  const [pool, setPool] = useState<PoolWire | null>(null);
  const [entries, setEntries] = useState<EntryWire[] | null>(null);
  const [rewards, setRewards] = useState<RewardLite[]>([]);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Add-entry form
  const [kind, setKind] = useState<EntryKind>("POINTS_BONUS");
  const [points, setPoints] = useState("10");
  const [stamps, setStamps] = useState("1");
  const [rewardId, setRewardId] = useState("");
  const [noPrizeMessage, setNoPrizeMessage] = useState("");
  const [weight, setWeight] = useState("10");
  const [displayLabel, setDisplayLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Preview
  const [previewCustomerId, setPreviewCustomerId] = useState("");
  const [previewResult, setPreviewResult] = useState<string | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);

  useEffect(() => {
    let abort = false;
    (async () => {
      setLoadErr(null);
      const [pr, er, rr] = await Promise.all([
        merchantFetch<{ pool: PoolWire }>("/api/v1/tenant/surprises/pool", {
          method: "GET",
        }),
        merchantFetch<{ entries: EntryWire[] }>(
          "/api/v1/tenant/surprises/pool/entries",
          { method: "GET" },
        ),
        merchantFetch<RewardLite[]>("/api/v1/tenant/rewards", { method: "GET" }),
      ]);
      if (abort) return;
      if (pr.ok) setPool(pr.data.pool);
      else setLoadErr(pr.message || "Couldn't load pool.");
      if (er.ok) setEntries(er.data.entries);
      if (rr.ok) setRewards(Array.isArray(rr.data) ? rr.data : []);
    })();
    return () => {
      abort = true;
    };
  }, [refreshKey]);

  async function patchPool(patch: Partial<PoolWire>) {
    const r = await merchantFetch<{ pool: PoolWire }>(
      "/api/v1/tenant/surprises/pool",
      { method: "PATCH", body: JSON.stringify(patch) },
    );
    if (r.ok) setPool(r.data.pool);
    else setLoadErr(r.message || "Couldn't update pool.");
  }

  async function addEntry(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    let entryPayload: Record<string, unknown>;
    if (kind === "POINTS_BONUS") {
      const p = parseInt(points, 10);
      if (!p || p < 1) return setSubmitErr("Points must be ≥ 1.");
      entryPayload = { kind, points: p };
    } else if (kind === "STAMPS_BONUS") {
      const s = parseInt(stamps, 10);
      if (!s || s < 1) return setSubmitErr("Stamps must be ≥ 1.");
      entryPayload = { kind, stamps: s };
    } else if (kind === "VOUCHER_REWARD") {
      if (!rewardId) return setSubmitErr("Pick a voucher.");
      entryPayload = { kind, rewardId };
    } else {
      entryPayload = { kind, ...(noPrizeMessage ? { message: noPrizeMessage } : {}) };
    }
    const w = parseInt(weight, 10);
    if (!w || w < 1 || w > 100) return setSubmitErr("Weight must be 1–100.");
    if (!displayLabel.trim()) return setSubmitErr("Display label required.");

    setSubmitting(true);
    const r = await merchantFetch<{ entry: EntryWire }>(
      "/api/v1/tenant/surprises/pool/entries",
      {
        method: "POST",
        body: JSON.stringify({
          entryKind: kind,
          entryPayload,
          weight: w,
          displayLabel: displayLabel.trim(),
        }),
      },
    );
    setSubmitting(false);
    if (r.ok) {
      setRefreshKey((k) => k + 1);
      setPoints("10");
      setStamps("1");
      setRewardId("");
      setNoPrizeMessage("");
      setWeight("10");
      setDisplayLabel("");
    } else {
      setSubmitErr(r.message || "Couldn't add entry.");
    }
  }

  async function toggleEntry(entry: EntryWire) {
    const r = await merchantFetch<{ entry: EntryWire }>(
      `/api/v1/tenant/surprises/pool/entries/${entry.id}`,
      {
        method: "PATCH",
        body: JSON.stringify({ enabled: !entry.enabled }),
      },
    );
    if (r.ok) setRefreshKey((k) => k + 1);
  }

  async function deleteEntry(entry: EntryWire) {
    if (
      !window.confirm(
        `Delete entry "${entry.displayLabel}"? If the entry has been picked before it will be disabled instead of removed.`,
      )
    )
      return;
    const r = await merchantFetch(
      `/api/v1/tenant/surprises/pool/entries/${entry.id}`,
      { method: "DELETE" },
    );
    if (r.ok) setRefreshKey((k) => k + 1);
  }

  async function runPreview() {
    setPreviewErr(null);
    setPreviewResult(null);
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        previewCustomerId,
      )
    ) {
      setPreviewErr("Sample customer ID must be a UUID.");
      return;
    }
    const r = await merchantFetch<{
      date: string;
      selected: EntryWire | null;
      totalEnabledWeight: number;
      rolledValue: number;
    }>(
      `/api/v1/tenant/surprises/preview?sampleCustomerId=${encodeURIComponent(previewCustomerId)}`,
      { method: "GET" },
    );
    if (!r.ok) {
      setPreviewErr(r.message || "Preview failed.");
      return;
    }
    if (!r.data.selected) {
      setPreviewResult(
        `Date ${r.data.date}: no enabled entries (rolled ${r.data.rolledValue}/${r.data.totalEnabledWeight}).`,
      );
    } else {
      setPreviewResult(
        `Date ${r.data.date}: would pick "${r.data.selected.displayLabel}" (${r.data.selected.entryKind}) — rolled ${r.data.rolledValue}/${r.data.totalEnabledWeight}.`,
      );
    }
  }

  return (
    <div className="min-h-screen pb-12">
      <div className="mx-auto max-w-2xl px-6 pt-8">
        <MerchantNav current="surprises" />
        <h1 className="mt-6 text-2xl font-bold text-navy">Surprise Pool</h1>
        <p className="mt-1 text-sm text-ink-dark">
          One surprise per customer per day, picked deterministically from
          this pool. No animations, no slot-machine framing.
        </p>

        {loadErr && (
          <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
            {loadErr}
          </p>
        )}

        {pool && (
          <section className="mt-6 rounded-2xl border border-ink-light/40 bg-white p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-navy">{pool.name}</p>
                <p className="text-xs text-ink-dark">
                  {pool.enabledEntryCount} enabled entries
                </p>
              </div>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={pool.enabled}
                    onChange={(e) => {
                      if (e.target.checked && pool.enabledEntryCount === 0) {
                        setLoadErr(
                          "Add at least one enabled entry before turning the pool on.",
                        );
                        return;
                      }
                      void patchPool({ enabled: e.target.checked });
                    }}
                  />
                  Pool on
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={pool.surprisePushEnabled}
                    onChange={(e) =>
                      void patchPool({ surprisePushEnabled: e.target.checked })
                    }
                  />
                  Push enabled
                </label>
              </div>
            </div>
          </section>
        )}

        <section className="mt-6">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
            Entries
          </h2>
          {entries === null ? (
            <p className="mt-2 text-sm text-ink-dark">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="mt-2 text-sm text-ink-dark">No entries yet.</p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="rounded-xl border border-ink-light/40 bg-white px-3 py-2 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-navy">
                      {e.displayLabel}
                    </p>
                    <p className="text-xs text-ink-dark">
                      {e.entryKind} · weight {e.weight}
                      {!e.enabled && " · disabled"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void toggleEntry(e)}
                      className="rounded-lg px-2 py-1 text-xs text-navy hover:bg-navy/5"
                    >
                      {e.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void deleteEntry(e)}
                      className="rounded-lg px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <form
          onSubmit={addEntry}
          className="mt-6 rounded-2xl border border-ink-light/40 bg-white p-4 flex flex-col gap-3"
        >
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
            Add entry
          </h2>
          <label className="text-sm">
            Kind
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as EntryKind)}
              className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
            >
              {KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {kind === "POINTS_BONUS" && (
            <label className="text-sm">
              Points
              <input
                type="number"
                min={1}
                max={100000}
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
              />
            </label>
          )}
          {kind === "STAMPS_BONUS" && (
            <label className="text-sm">
              Stamps
              <input
                type="number"
                min={1}
                max={50}
                value={stamps}
                onChange={(e) => setStamps(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
              />
            </label>
          )}
          {kind === "VOUCHER_REWARD" && (
            <label className="text-sm">
              Voucher (from your rewards)
              <select
                value={rewardId}
                onChange={(e) => setRewardId(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
              >
                <option value="">— pick one —</option>
                {rewards
                  .filter((r) => r.active)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {kind === "NO_PRIZE" && (
            <label className="text-sm">
              Optional message (≤ 160 chars)
              <input
                type="text"
                maxLength={160}
                value={noPrizeMessage}
                onChange={(e) => setNoPrizeMessage(e.target.value)}
                className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
                placeholder="Better luck tomorrow!"
              />
            </label>
          )}

          <label className="text-sm">
            Weight (1–100)
            <input
              type="number"
              min={1}
              max={100}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
            />
          </label>

          <label className="text-sm">
            Display label
            <input
              type="text"
              maxLength={80}
              value={displayLabel}
              onChange={(e) => setDisplayLabel(e.target.value)}
              className="mt-1 block w-full rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
              placeholder='e.g. "10 bonus points"'
            />
          </label>

          {submitErr && (
            <p className="text-sm text-red-600">{submitErr}</p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg bg-navy px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
          >
            {submitting ? "Adding…" : "Add entry"}
          </button>
        </form>

        <section className="mt-6 rounded-2xl border border-ink-light/40 bg-white p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
            Preview today's selection
          </h2>
          <p className="mt-1 text-xs text-ink-dark">
            Enter a sample customer UUID to see what they'd be shown today.
            Read-only — no claim is created.
          </p>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={previewCustomerId}
              onChange={(e) => setPreviewCustomerId(e.target.value)}
              placeholder="customer UUID"
              className="flex-1 rounded-lg border border-ink-light/40 px-2 py-1.5 text-sm"
            />
            <button
              type="button"
              onClick={() => void runPreview()}
              className="rounded-lg bg-navy px-3 py-1.5 text-sm font-semibold text-white"
            >
              Preview
            </button>
          </div>
          {previewErr && (
            <p className="mt-2 text-sm text-red-600">{previewErr}</p>
          )}
          {previewResult && (
            <p className="mt-2 text-sm text-ink-dark">{previewResult}</p>
          )}
        </section>
      </div>
    </div>
  );
}
